import { useEffect, useMemo, useState } from 'react';

const getHeight = ({
  mobilePosition,
  popupOpen,
  popupHeight,
  choicePopupOpen,
  navigationSettings,
  navigationSharingPopupOpen,
  actionPathFinished,
}) => {
  if (mobilePosition > 1) {
    const maxPixels = popupHeight + 102;

    return popupOpen && popupHeight && maxPixels < window.innerHeight ? `${maxPixels}px` : '95%';
  }

  if (mobilePosition < 1) {
    if (!popupOpen && !choicePopupOpen) {
      return '15%';
    }

    return '31%';
  }

  if (choicePopupOpen && mobilePosition === 1) {
    return 'calc(31% + 88px)';
  }

  if (choicePopupOpen && navigationSettings?.detailedRouteEnabled) {
    return '50%';
  }

  if (navigationSharingPopupOpen && actionPathFinished) {
    return '31%';
  }

  return '40%';
};

export const useStyles = ({
  isBackgroundVisible,
  navigationSharingPopupOpen,
  actionPathFinished,
  choicePopupOpen,
  mobilePosition,
  popupHeight,
  navigationSettings,
  popupOpen,
}) => {
  const mobileHeight = useMemo(
    () =>
      getHeight({
        actionPathFinished,
        choicePopupOpen,
        mobilePosition,
        navigationSettings,
        navigationSharingPopupOpen,
        popupHeight,
        popupOpen,
      }),
    [
      actionPathFinished,
      choicePopupOpen,
      mobilePosition,
      navigationSettings,
      navigationSharingPopupOpen,
      popupHeight,
      popupOpen,
    ],
  );

  const presentationStyle = useMemo(
    () => ({
      width: { xs: '100%', lg: '100%' },
      height: {
        xs:
          isBackgroundVisible || (navigationSharingPopupOpen && actionPathFinished)
            ? mobileHeight
            : '0px',
        sm: '100%',
      },
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: '76px',
      alignSelf: 'center',
      position: { xs: 'fixed', sm: 'relative' },
      bottom: {
        xs:
          isBackgroundVisible || (navigationSharingPopupOpen && actionPathFinished) ? 0 : '-100px',
        sm: 0,
      },
      borderRadius: { xs: '20px 20px 0 0', sm: 0 },
      background: { xs: '#F7F7F7', sm: 'transparent' },
      padding: { xs: '14px', sm: 0 },
      marginBottom: { sm: '110px' },
      overflowY: { sm: 'hidden' },
      visibility: {
        xs:
          isBackgroundVisible || (navigationSharingPopupOpen && actionPathFinished)
            ? 'visible'
            : 'hidden',
        sm: 'unset',
      },
      maxHeight: {
        xs: '95%',
        sm: '100%',
      },
      transition: 'height 0.3s',
    }),
    [actionPathFinished, isBackgroundVisible, mobileHeight, navigationSharingPopupOpen],
  );

  const mainWrapperStyle = useMemo(
    () => ({
      position: 'absolute',
      width: '100%',
      height: '100dvh',
      background: 'transparent',
      zIndex: 1200,
      top: 0,
      pointerEvents: isBackgroundVisible ? 'auto' : 'none !important',
      overflowY: {
        xs: 'hidden',
        sm: mobilePosition === 0 || popupOpen ? 'hidden' : '',
      },
      overflowX: 'hidden',
      justifyContent: 'space-between',
    }),
    [isBackgroundVisible, mobilePosition, popupOpen],
  );

  const whiteLineStyle = useMemo(
    () => ({
      background: '#fff',
      width: '47px',
      height: '6px',
      borderRadius: '26px',
      display: { xs: 'flex', sm: 'none' },
    }),
    [],
  );
  const whiteLineWrapperStyle = useMemo(
    () => ({
      background: 'transparent',
      width: '57px',
      height: '24px',
      // borderRadius: '26px',
      position: 'absolute',
      top: '-20px',
      alignItems: 'center',
      justifyContent: 'center',
      display: { xs: 'flex', sm: 'none' },
      zIndex: 9999,
    }),
    [],
  );

  return { presentationStyle, mainWrapperStyle, whiteLineStyle, whiteLineWrapperStyle };
};

export const useCategoriesStyles = () => {
  const calculateHeight = () => `${0.95 * window.innerHeight - 250}px`;

  const [dynamicHeight, setDynamicHeight] = useState(calculateHeight);

  useEffect(() => {
    const handleResize = () => {
      setDynamicHeight(calculateHeight());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const categoriesGridWrapperStyle = useMemo(
    () => ({
      // gap: '10px',
      maxHeight: { xs: dynamicHeight, sm: '618px' },
      overflowY: 'auto',
      zIndex: 10,
      '&::-webkit-scrollbar': {
        width: '4px',
        backgroundColor: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'transparent',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
      '&:hover': {
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#BDBDBD',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#fff',
        },
      },
    }),
    [dynamicHeight],
  );

  const gridItemInnerStyle = useMemo(
    () => ({
      overflow: 'hidden',
      gap: '6px',
      alignItems: 'center',
      justifyContent: 'start',
      p: { xs: '10px', lg: '14px 14px 0px 14px' },
      maxWidth: { sm: '154px !important' },
      width: { sm: '154px' },
      height: '94px',
      display: 'flex !important',
      borderRadius: '8px',
      border: '1px solid #EAECF0',
      background: '#FFF',
      cursor: 'pointer',
      flexDirection: 'column',
    }),
    [],
  );

  const gridItemTitleStyle = useMemo(
    () => ({
      fontSize: '14px',
      fontWeight: { xs: 400, sm: 500 },
      fontFamily: 'Inter, Heebo, sans-serif',
      textAlign: 'center',
      whiteSpace: 'break-spaces',
      width: 'inherit',
      textOverflow: 'ellipsis',
      p: { xs: '0 10px', lg: '0px' },
      lineHeight: 'normal',
      wordBreak: 'break-word',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
    }),
    [],
  );
  return { categoriesGridWrapperStyle, gridItemInnerStyle, gridItemTitleStyle };
};

export const ZoomButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  fontWeight: 500, // font-medium
  transition: 'all 0.2s ease-in-out', // transition-colors approximation
  outline: 'none',
  '&:focus-visible': {
    outline: 'none',
  },
  '&:disabled': {
    pointerEvents: 'none',
    opacity: 0.5,
  },
  '& svg': {
    pointerEvents: 'none',
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  backgroundColor: '#fff',
  height: 40,
  width: 40,
};
